/**
 * Computer Icon Renderer - Asset node icon
 *
 * Renders a laptop/computer icon used for representing asset nodes
 * in the graph visualization.
 */

import { getIconConfig } from '@/components/icons/nodeIcons/config/iconConfig';
import { getThemeColors } from '@/components/icons/nodeIcons/config/themeColors';
import {
  calculateIconPosition,
  drawCircleBackground,
  setupCanvas,
} from '@/components/icons/nodeIcons/renderers/base';

/**
 * Create computer icon data URL
 *
 * Generates a computer/laptop icon with circular background for use as
 * a node image in the graph visualization.
 *
 * @param currentTheme - Current theme ('light' or 'dark')
 * @param isHighlighted - Whether the icon should appear highlighted
 * @returns Data URL string of the rendered icon
 */
export const createComputerIconDataURL = (
  currentTheme: 'light' | 'dark',
  isHighlighted: boolean = false
): string => {
  const config = getIconConfig('computer');
  const colors = getThemeColors('computer', currentTheme, isHighlighted);
  const { canvas, ctx, size, centerX, centerY } = setupCanvas(config);

  // Draw circle background and border
  drawCircleBackground(ctx, centerX, centerY, size, config.scale, colors);

  // Calculate icon position
  const {
    x: laptopX,
    y: laptopY,
    scale: laptopScale,
  } = calculateIconPosition(centerX, centerY, size, config);

  ctx.fillStyle = colors.icon;

  // Start drawing the laptop path
  ctx.save();
  ctx.translate(laptopX, laptopY);
  ctx.scale(laptopScale, laptopScale);

  // Draw the SVG path (scaled to our canvas)
  ctx.beginPath();

  // Main body
  ctx.moveTo(23.3333, 15.75);
  ctx.lineTo(4.66667, 15.75);
  ctx.bezierCurveTo(3.38333, 15.75, 2.33333, 14.7375, 2.33333, 13.5);
  ctx.lineTo(2.33333, 2.25);
  ctx.bezierCurveTo(2.33333, 1.0125, 3.38333, 0, 4.66667, 0);
  ctx.lineTo(23.3333, 0);
  ctx.bezierCurveTo(24.6167, 0, 25.6667, 1.0125, 25.6667, 2.25);
  ctx.lineTo(25.655, 13.5);
  ctx.bezierCurveTo(25.655, 14.7375, 24.6167, 15.75, 23.3333, 15.75);
  ctx.closePath();

  // Base/stand part
  ctx.moveTo(28, 18);
  ctx.lineTo(0, 18);
  ctx.lineTo(0, 15.75);
  ctx.lineTo(4.66667, 15.75);
  ctx.lineTo(23.3333, 15.75);
  ctx.lineTo(28, 15.75);
  ctx.lineTo(28, 18);
  ctx.closePath();

  // Screen part
  ctx.moveTo(23.3333, 13.5);
  ctx.lineTo(4.66667, 13.5);
  ctx.lineTo(4.66667, 2.25);
  ctx.lineTo(23.3333, 2.25);
  ctx.lineTo(23.3333, 13.5);
  ctx.closePath();

  ctx.fill();
  ctx.restore();

  // Add screen detail (slightly darker)
  const screenWidth = 18.5 * laptopScale;
  const screenHeight = 11 * laptopScale;
  const screenX = centerX - screenWidth / 2;
  const screenY = centerY - screenHeight / 2 - laptopScale * 0.5;

  // Screen color (slightly darker gray)
  ctx.fillStyle =
    currentTheme === 'light' ? 'rgb(230, 230, 230)' : 'rgb(50, 50, 70)';
  ctx.fillRect(screenX, screenY, screenWidth, screenHeight);

  // Convert to data URL with higher quality
  return canvas.toDataURL('image/png', 1.0);
};
